import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { App } from '@gin-betting/core';

import { SELECTOR_STUB } from 'app-constants';
import { useAsyncModule } from 'async-modules';
import { State, Modules } from 'types';
import { formatDecimalOdd } from 'utils';
import ui from 'modules/ui';

const {
    selectors: { selectOddFormat },
} = ui;

interface Output {
    betStatus: App.BetslipStatus;
    totalOdd: ReturnType<typeof formatDecimalOdd> | null;
    betsSize: number;
}

const defaultOutput: Output = {
    betStatus: App.BetslipStatus.INITIAL,
    totalOdd: null,
    betsSize: 0,
};

const makePropsSelector = (betslipModule: Modules['betslip']['default']) => {
    const {
        selectors: {
            selectCachedBetslipBetWithStatus,
            selectCachedBetslipBetTotals,
        },
    } = betslipModule;

    return createSelector(
        selectCachedBetslipBetWithStatus,
        selectCachedBetslipBetTotals,
        selectOddFormat,
        (betslipBet, totals, oddFormat) => {
            if (!betslipBet) return null;

            const { totalOdd } = totals;
            const { betslipOddIds, status: betStatus } = betslipBet;

            return {
                totalOdd: totalOdd
                    ? formatDecimalOdd(totalOdd, oddFormat)
                    : null,
                betStatus,
                betsSize: betslipOddIds.count(),
            };
        }
    );
};

const useBetslipInfo = (): Output => {
    const betslipModule = useAsyncModule<'betslip'>('betslip');

    const selectProps = useMemo(
        () =>
            betslipModule ? makePropsSelector(betslipModule) : SELECTOR_STUB,
        [betslipModule]
    );

    const selectChosenBetslipBetId = useMemo(
        () =>
            betslipModule?.selectors.selectChosenBetslipBetId ?? SELECTOR_STUB,
        [betslipModule]
    );

    const betslipBetId = useSelector((s: State) => selectChosenBetslipBetId(s));

    const props = useSelector((s: State) => selectProps(s, { betslipBetId }));

    if (!props) return defaultOutput;

    return props;
};

export default useBetslipInfo;
